/**
 * ProfileCompletionCard — actionable replacement for the bare ProgressBar
 * that used to sit on the profile screen.
 *
 * Shows the headline percentage + a tappable row for the highest-impact
 * missing field ("Verificá tu identidad →"). Tapping the row pushes the
 * caller to the relevant route (edit-profile / verification). A "Ver
 * todos" disclosure expands the full list of remaining gaps so users can
 * jump straight to the field they care about most without going through
 * Edit Profile to find it.
 *
 * Renders nothing when `completion.percent === 100`.
 */
import { useState } from 'react'
import { View, Text } from 'react-native'
import { useTranslation } from 'react-i18next'
import { useRouter } from 'expo-router'
import { ChevronRight, ChevronDown, ChevronUp, CheckCircle2 } from '@/components/ui/icons'
import { Pressable } from './Pressable'
import { ProgressBar } from './ProgressBar'
import { useSemanticColors, SPACING, INTERACTION } from '@/lib/theme/tokens'
import { COLORS, FONTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import type { ProfileCompletion, ProfileCompletionStep } from '@/lib/hooks/use-profile'

interface ProfileCompletionCardProps {
  completion: ProfileCompletion
  testID?: string
}

export function ProfileCompletionCard({ completion, testID }: ProfileCompletionCardProps) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const router = useRouter()
  const [expanded, setExpanded] = useState(false)

  if (completion.percent >= 100 || completion.missingFields.length === 0) {
    return null
  }

  const [primary, ...rest] = completion.missingFields
  if (!primary) return null

  const renderRow = (field: ProfileCompletionStep, isPrimary: boolean) => (
    <Pressable
      key={field.key}
      onPress={() => router.push(field.route as never)}
      accessibilityRole="button"
      accessibilityLabel={t(field.labelKey)}
      testID={`profile.completion.${field.key}`}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: SPACING.sm,
        // Primary row gets a hair more vertical padding so it reads as the
        // anchor action, secondary rows pack tighter inside the disclosure.
        paddingVertical: isPrimary ? SPACING.sm + SPACING.xs / 2 : SPACING.sm,
        minHeight: 44,
      }}
    >
      <View
        style={{
          width: 28,
          height: 28,
          borderRadius: 14,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isPrimary ? `${COLORS.coral.DEFAULT}1A` : `${colors.textMuted}14`,
        }}
      >
        <CheckCircle2
          size={16}
          color={isPrimary ? COLORS.coral.DEFAULT : colors.textMuted}
          strokeWidth={isPrimary ? 2.2 : 1.8}
        />
      </View>
      <Text
        style={{
          flex: 1,
          fontSize: isPrimary ? 14 : 13,
          fontFamily: isPrimary ? FONTS.bodyBold : FONTS.body,
          color: colors.textPrimary,
        }}
        numberOfLines={2}
      >
        {t(field.labelKey)}
      </Text>
      <ChevronRight size={16} color={colors.textMuted} />
    </Pressable>
  )

  return (
    <View
      testID={testID}
      style={{
        paddingHorizontal: SPACING.md,
        paddingTop: SPACING.md,
        // The disclosure toggle at the bottom carries a 44pt touch target
        // around a 12pt label, so it already contributes ~13pt of its own
        // slack under the last line of ink. A full SPACING.md on top of that
        // left the card visibly bottom-heavy against its 16pt top padding.
        paddingBottom: SPACING.xs,
        gap: SPACING.xs,
      }}
    >
      {/* No `showPercentage`: the label already reads "Perfil 35% completo",
          so the right-hand value printed the same number twice on one line. */}
      <ProgressBar
        value={completion.percent / 100}
        label={t('profile.profile_completion', { percent: completion.percent })}
        fillColor={COLORS.coral.DEFAULT}
        height={6}
      />

      {/* Primary nudge — always visible */}
      {renderRow(primary, true)}

      {/* Disclosure for remaining missing fields. Hidden when there's only
          one gap left (the primary already covers it). */}
      {rest.length > 0 ? (
        <>
          {expanded ? (
            <View style={{ gap: SPACING.xs }}>
              <View
                style={{
                  height: 1,
                  backgroundColor: colors.border,
                  marginVertical: SPACING.xs,
                }}
              />
              {rest.map((field) => renderRow(field, false))}
            </View>
          ) : null}
          <Pressable
            onPress={() => setExpanded((v) => !v)}
            accessibilityRole="button"
            accessibilityLabel={t(
              expanded ? 'profile.completion.see_less' : 'profile.completion.see_all',
              { count: rest.length },
            )}
            testID="profile.completion.toggle"
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              alignSelf: 'flex-start',
              gap: SPACING.xs,
              // A 12px label with 4px of padding is a 23pt target.
              minHeight: INTERACTION.minTouchTarget,
              paddingVertical: SPACING.xs,
            }}
          >
            <Text style={{ fontSize: 12, fontFamily: FONTS.body, color: colors.textSecondary }}>
              {expanded
                ? t('profile.completion.see_less')
                : t('profile.completion.see_all', { count: rest.length })}
            </Text>
            {expanded ? (
              <ChevronUp size={14} color={colors.textSecondary} />
            ) : (
              <ChevronDown size={14} color={colors.textSecondary} />
            )}
          </Pressable>
        </>
      ) : null}
    </View>
  )
}
